import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/content";
import { usePaymentPoll } from "../hooks/usePaymentPoll";

const CheckingView = () => (
  <div data-testid="payment-checking">
    <Loader2 className="h-14 w-14 text-blue-500 mx-auto mb-5 animate-spin" />
    <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">Confirming your payment...</h1>
    <p className="text-zinc-400">Hang tight — this only takes a few seconds.</p>
  </div>
);

const PaidView = () => (
  <div data-testid="payment-paid">
    <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto mb-5" />
    <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">Payment Received!</h1>
    <p className="text-zinc-400 leading-relaxed mb-8">
      Your payment went through and we've got it on file. Mason's will contact
      you shortly to confirm the details.
    </p>
    <a
      data-testid="payment-success-call"
      href={PHONE_TEL}
      className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 py-3.5 font-semibold transition-colors"
    >
      <Phone className="h-4 w-4" /> Questions? Call {PHONE_DISPLAY}
    </a>
  </div>
);

const ProblemView = ({ state }) => (
  <div data-testid="payment-problem">
    <XCircle className="h-14 w-14 text-red-500 mx-auto mb-5" />
    <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">
      {state === "timeout" ? "Still processing" : "Something went wrong"}
    </h1>
    <p className="text-zinc-400 leading-relaxed mb-8">
      {state === "timeout"
        ? "Your payment may still be processing. If you were charged, we'll see it and reach out — or give us a quick call to confirm."
        : "We couldn't confirm this payment. If you were charged, call us and we'll sort it out right away."}
    </p>
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <a href={PHONE_TEL} className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 py-3 font-semibold transition-colors">
        <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
      </a>
      <Link to="/" className="inline-flex items-center justify-center border border-zinc-700 hover:border-white text-white rounded-sm px-6 py-3 font-semibold transition-colors">
        Back to Home
      </Link>
    </div>
  </div>
);

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const state = usePaymentPoll(searchParams.get("session_id"));

  return (
    <main data-testid="payment-success-page" className="pt-20 md:pt-24 min-h-[80vh] flex items-center justify-center px-6">
      <div className="max-w-lg w-full bg-[#121212] border border-zinc-800 rounded-xl p-10 text-center my-16">
        {state === "checking" && <CheckingView />}
        {state === "paid" && <PaidView />}
        {(state === "error" || state === "expired" || state === "timeout") && <ProblemView state={state} />}
      </div>
    </main>
  );
}
